import { NavLink } from 'react-router-dom';
import { FolderOpen, Settings, Key, Users, LayoutDashboard, FileText, ArrowRightLeft, KeyRound, CreditCard, BookOpen, ExternalLink, Puzzle, Shield, ShieldCheck } from 'lucide-react';
import { motion } from 'motion/react';
import { ProjectSelector } from './ProjectSelector';
import { UserMenu } from './UserMenu';
import { useProjects } from '../../hooks/useProjects';
import { useAdminCheck } from '../../hooks/useAdmin';
import { cn } from '@/lib/utils';

interface SidebarProps {
  onCreateProject?: () => void;
  isCreateProjectDisabled?: boolean;
}

export function Sidebar({ onCreateProject, isCreateProjectDisabled }: SidebarProps) {
  const { selectedProject } = useProjects();
  const { isAdmin } = useAdminCheck();

  return (
    <aside className="w-60 bg-card/50 backdrop-blur-xl border-r border-border/50 flex flex-col h-full">
      <div className="h-14 flex items-center px-4 border-b border-border/50">
        <NavLink to="/" className="flex items-center gap-2.5 group">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="h-8 w-8 bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center shadow-lg shadow-primary/25 transition-shadow group-hover:shadow-primary/40"
          >
            <Key className="h-4.5 w-4.5 text-primary-foreground" />
          </motion.div>
          <span className="text-sm font-semibold tracking-tight">KeyEnv</span>
        </NavLink>
      </div>

      <div className="p-3 border-b border-border/50">
        <ProjectSelector onCreateProject={onCreateProject} isCreateProjectDisabled={isCreateProjectDisabled} />
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        <NavItem to="/projects" icon={FolderOpen}>Projects</NavItem>

        {selectedProject && (
          <div className="mt-5 pt-5 border-t border-border/50">
            <div className="px-3 mb-3 flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-primary animate-glow-pulse" />
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider truncate">
                {selectedProject.name}
              </span>
            </div>
            <div className="space-y-0.5">
              <NavItem to={`/projects/${selectedProject.id}`} icon={LayoutDashboard}>Overview</NavItem>
              <NavItem to={`/projects/${selectedProject.id}/secrets`} icon={Key}>Secrets</NavItem>
              <NavItem to={`/projects/${selectedProject.id}/compare`} icon={ArrowRightLeft}>Compare</NavItem>
              <NavItem to={`/projects/${selectedProject.id}/audit`} icon={FileText}>Audit</NavItem>
              <NavItem to={`/projects/${selectedProject.id}/team`} icon={Users}>Team</NavItem>
              <NavItem to={`/projects/${selectedProject.id}/integrations`} icon={Puzzle}>Integrations</NavItem>
              <NavItem to={`/projects/${selectedProject.id}/scans`} icon={Shield}>Scans</NavItem>
            </div>
          </div>
        )}

        <div className="mt-5 pt-5 border-t border-border/50">
          <div className="space-y-0.5">
            <NavItem to="/tokens" icon={KeyRound}>API Tokens</NavItem>
            <NavItem to="/billing" icon={CreditCard}>Billing</NavItem>
            <NavItem to="/settings" icon={Settings}>Settings</NavItem>
            {isAdmin && <NavItem to="/admin" icon={ShieldCheck}>Admin</NavItem>}
            <ExternalNavItem href={import.meta.env.VITE_DOCS_URL || 'https://keyenv.dev/docs'} icon={BookOpen}>
              Docs
            </ExternalNavItem>
          </div>
        </div>
      </nav>

      <div className="p-3 border-t border-border/50">
        <UserMenu />
      </div>
    </aside>
  );
}

function NavItem({ to, icon: Icon, children }: { to: string; icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 group',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.div
              layoutId="activeIndicator"
              className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 bg-primary rounded-r-full"
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          )}
          <Icon className={cn(
            "h-4 w-4 flex-shrink-0 transition-transform duration-200",
            !isActive && "group-hover:scale-110"
          )} />
          <span className="truncate">{children}</span>
        </>
      )}
    </NavLink>
  );
}

function ExternalNavItem({ href, icon: Icon, children }: { href: string; icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="relative flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 group text-muted-foreground hover:bg-muted/40 hover:text-foreground"
    >
      <Icon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
      <span className="truncate flex-1">{children}</span>
      <ExternalLink className="h-3 w-3 opacity-50" />
    </a>
  );
}
